//@flow
import React from "react"

import type { FileDropProps, FileDropState } from "./types"

const withFileDrop = (
  WrappedComponent: Class<React$Component<*, *>>
): Class<React$Component<*, *>> =>
  class FileDrop extends React.Component<FileDropProps, FileDropState> {
    dropTarget: ?HTMLElement

    constructor(props: FileDropProps) {
      super(props)
      this.dropTarget = null
      this.state = { files: null, filePreviews: [] }
    }

    componentWillUnmount() {
      this.dropTarget && this.removeListeners(this.dropTarget)
    }

    addListeners = (element: HTMLElement) => {
      element.addEventListener("dragenter", this.dragenter, false)
      element.addEventListener("dragover", this.dragover, false)
      element.addEventListener("drop", this.drop, false)
    }

    removeListeners = (element: HTMLElement) => {
      element.removeEventListener("dragenter", this.dragenter)
      element.removeEventListener("dragover", this.dragover)
      element.removeEventListener("drop", this.drop)
    }

    setDropTarget = (element: HTMLElement) => {
      this.dropTarget && this.removeListeners(this.dropTarget)
      this.dropTarget = element
      this.dropTarget && this.addListeners(this.dropTarget)
    }

    dragenter = (e: DragEvent) => {
      e.stopPropagation()
      e.preventDefault()
    }

    dragover = (e: DragEvent) => {
      e.stopPropagation()
      e.preventDefault()
    }

    drop = (e: DragEvent) => {
      e.stopPropagation()
      e.preventDefault()
      e.dataTransfer && this.handleFiles(e.dataTransfer.files)
    }

    handleFiles = async (files: FileList) => {
      const { onDrop, validate, generatePreviews } = this.props

      const allowedFiles = Array.from(files).filter((file, index) => {
        return validate ? validate(file, index) : true
      })

      const filePreviews: ?Array<URL> = generatePreviews
        ? await Promise.all(
            allowedFiles.map(async file => {
              return await this.loadFile(file)
            })
          )
        : null

      this.setState(() => ({ files: allowedFiles, filePreviews }))

      onDrop && allowedFiles && onDrop(allowedFiles)
    }

    loadFile = async (file: File): Promise<URL> =>
      new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e: { target: { result: ?URL } }) => {
          e.target.result
            ? resolve(e.target.result)
            : reject(new Error("Could not load file."))
        }
        reader.readAsDataURL(file)
      })

    render() {
      const { filePreviews, files } = this.state
      const { loading, errorMessage, ...otherProps } = this.props
      return (
        <WrappedComponent
          setDropTarget={this.setDropTarget}
          files={files}
          filePreviews={filePreviews}
          loading={loading}
          errorMessage={errorMessage}
          {...otherProps}
        />
      )
    }
  }

export default withFileDrop



// WEBPACK FOOTER //
// ./src/UI/FileUploader/withFileDrop.js